import { animate, style, transition, trigger } from '@angular/animations';
import { DOCUMENT, Location, NgClass, NgOptimizedImage, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { StoreSpecificConfig, StoreSpecificConfigService } from '@scalefast/config-angular';

import { StaticFileHash } from '@scalefast/ecommerce-core';

import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { delay, from, tap } from 'rxjs';
import { CustomBuilderComponent } from 'src/app/core/decorators';
import { getObservedEntriesMaxHeight } from 'src/app/shared/helpers/dom.helper';

export const modalCarrouselComponentConfig = {
  tag: 'app-cc-carrousel',
  name: 'slider with modals',
  inputs: [
    {
      name: 'data',
      type: 'list',
      subFields: [
        {
          name: 'header_text',
          type: 'string',
        },
        {
          name: 'slide_text',
          type: 'richText',
        },
        {
          name: 'footer_text',
          type: 'string',
        },
        {
          name: 'image',
          type: 'file',
          required: true,
        },
        {
          name: 'image_mobile',
          type: 'file',
          required: true,
        },
        {
          name: 'youtube_iframe',
          type: 'boolean',
        },
        {
          name: 'youtube_iframe_url',
          type: 'string',
          helperText:
            'Fill with the embed link provided on Youtube share option, embed. Example: https://www.youtube.com/embed/...',
        },
        {
          name: 'button_text',
          type: 'string',
        },
        {
          name: 'button_link',
          type: 'string',
          helperText:
            'Fill in with an internal or external link. For internal links only relative routes without language are allowed. Example: /spanish-route will go to /es-es/spanish-route. For external links only absolute routes are allowed. Example: https://google.com',
        },
        {
          name: 'button_text_2',
          type: 'string',
        },
        {
          name: 'button_link_2',
          type: 'string',
        },
      ],
    },
    {
      name: 'title',
      type: 'string',
    },
    {
      name: 'subtitle',
      type: 'richText',
    },
    {
      name: 'button_text',
      type: 'string',
    },
    {
      name: 'button_link',
      type: 'string',
    },
  ],
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  selector: 'app-cc-carrousel',
  templateUrl: './modal-carrousel.compoment.html',
  styleUrls: ['./modal-carrousel.styles.scss'],
  imports: [NgStyle, NgClass, NgOptimizedImage, StaticFileHash, RouterLink],
  animations: [
    trigger('modalAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(-50px)' }),
        animate('300ms ease-out', style({ transform: 'translateY(0)' })),
      ]),
      transition(':leave', [animate('300ms ease-in', style({ transform: 'translateY(-50px)' }))]),
    ]),
    trigger('modalBackgroundAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('150ms ease-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
@CustomBuilderComponent(modalCarrouselComponentConfig)
export class AppModalCarrouselComponent implements OnInit, AfterViewInit, OnDestroy {
  // Builder data comes in 2 layers and build the component twice,
  // for first run this is [object,object] and on the second run its an parsed object.
  @Input()
  data: any;
  @Input() title: any;
  @Input() subtitle: any;
  @Input() button_text: any;
  @Input() button_link: any;
  @Input() button_text_2: any;
  @Input() button_link_2: any;

  modalOpen = signal(false);
  // TODO: All this can be removed and use native css when allow-discrete is available on every browser https://developer.mozilla.org/en-US/docs/Web/CSS/transition-behavior
  // Workaround to delay 10ms the modal open so transform animation can be triggered
  delayedModalOpen = signal(false);
  #router = inject(Router);
  #storeSpecificConfigService = inject(StoreSpecificConfigService);

  parsedData: {
    image: string;
    image_mobile: string;
    header_text: string;
    slide_text: string;
    footer_text: string;
    youtube_iframe: boolean;
    youtube_iframe_url: string;
    button_text: string;
    button_link: string;
    button_text_2: string;
    button_link_2: string;
    external_link: string;
    external_link_2: string;
  }[] = [];

  @ViewChild('sliderRef') sliderRef!: ElementRef<HTMLElement>;
  video_src: SafeResourceUrl = '';

  slider?: KeenSliderInstance;
  titleObserver?: ResizeObserver;
  linkObserver?: ResizeObserver;

  currentSlide = signal(0);
  dotHelper: Array<Number> = [];
  arrowTop = signal(0);
  titleHeight = signal<string | undefined>(undefined);
  linkHeight = signal<string | undefined>(undefined);
  uniqueId: string;
  private location = inject(Location);

  constructor(
    private domSanitizerService: DomSanitizer,
    @Inject(DOCUMENT) private _doc: Document,
  ) {
    this.uniqueId = crypto.randomUUID();
    effect(() => {
      const modalState = this.modalOpen();
      from([modalState])
        .pipe(
          delay(10),
          tap((state) => {
            this.delayedModalOpen.set(state); // Update delayed signal after the delay
          }),
        )
        .subscribe();
    });
  }

  ngOnInit() {
    if (typeof this.data === 'object') {
      this.parsedData = this.data;
    }
  }

  ngAfterViewInit(): void {
    if (this.parsedData.length > 0) {
      this.slider = new KeenSlider(this.sliderRef.nativeElement, {
        loop: true,
        breakpoints: {
          '(max-width: 768px)': {
            slides: {
              origin: 'center',
              perView: () => {
                if (this.parsedData.length === 1) {
                  return 1;
                } else {
                  return 1.5;
                }
              },
            },
          },
        },
        initial: this.currentSlide(),
        slideChanged: (s) => {
          this.currentSlide.set(s.track.details.rel);
        },
      });
      this.dotHelper = [...Array(this.slider.track.details.slides.length).keys()];
    }
    const observer = new ResizeObserver((entries) => {
      this.setArrowTop(entries[0].contentRect.height);
    });
    if (this._doc.querySelector('.keen-slider__image')) {
      observer.observe(this._doc.querySelector('.keen-slider__image')!);
    }

    const titles = this._doc.querySelectorAll('.slide__title-' + this.uniqueId);

    //FIXME: We may be able to remove this observer and use css grid for the title height
    // Otherwise we should review the code below as it's never discarding/resetting fully
    // FIXME: If we keep the observer, it can still be throttled to avoid performance issues
    this.titleObserver = new ResizeObserver((entries) => {
      this.titleHeight.set(getObservedEntriesMaxHeight(entries));
      console.log('title entries', entries, this.titleHeight());
    });

    if (titles) {
      titles.forEach((title) => {
        // safe non-null assert, typescript doesn't understand foreach is synchronous
        this.titleObserver!.observe(title);
      });
    }

    const links = this._doc.querySelectorAll('.keen-slider__button__link-' + this.uniqueId);
    this.linkObserver = new ResizeObserver((entries) => {
      this.linkHeight.set(getObservedEntriesMaxHeight(entries));
    });

    if (links) {
      links.forEach((link) => {
        // safe non-null assert, typescript doesn't understand foreach is synchronous
        this.linkObserver!.observe(link);
      });
    }
  }

  ngOnDestroy() {
    this.slider?.destroy();
    this.titleObserver?.disconnect();
    this.linkObserver?.disconnect();
  }

  setArrowTop(imgHeight: number) {
    this.arrowTop.set(imgHeight / 2);
  }

  showYoutubeIframe(isEnabled: boolean, url: string) {
    if (isEnabled) {
      this.video_src = this.domSanitizerService.bypassSecurityTrustResourceUrl(url);
      this.modalOpen.set(true);
    }
  }

  modalClose() {
    this.modalOpen.set(false);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.modalClose();
    }
  }

  /**
   * This function redirect to the correct url.
   * It uses Angular router for internal links and window.location for external redirections.
   * FIXME: the routing related functions need to be moved to a generic file once they are stable
   */
  hybridGoToPage(event: Event, buttonLink: string): void {
    // prevent href event to be launched by browser
    event.preventDefault();
    if (this.isV4Link(buttonLink)) {
      const externalV4Link = this.location.prepareExternalUrl(buttonLink);
      window.location.assign(externalV4Link);
    } else if (this.isExternalLink(buttonLink)) {
      window.location.assign(buttonLink);
    } else {
      this.#router.navigateByUrl(buttonLink);
    }
  }

  /**
   * Check if the link is an external url (with protocol)
   */
  private isExternalLink(link: string): boolean {
    return link.startsWith('http://') || link.startsWith('https://');
  }

  /**
   * Check if the link is included in the array of v4 links in storeSpecificConfig
   */
  private isV4Link(link: string): boolean {
    let storeSpecificConfig: StoreSpecificConfig = this.#storeSpecificConfigService.get();
    const externalExceptions = storeSpecificConfig.hybridV4Paths;
    return externalExceptions.some((exception: any) => {
      return exception.startsWith(link);
    });
  }

  /**
   * Get the correct href for internal or external link for SEO/disabled js purpose
   */
  getHref(buttonLink: string): string {
    if (this.isV4Link(buttonLink)) {
      return this.location.prepareExternalUrl(buttonLink);
    } else {
      return buttonLink;
    }
  }
}
